// ============================================================================
// governanceRows — lignes read-only pour l'ecran Acces & gouvernance (lot 4)
// ============================================================================
// Agregation CLIENT des 3 modeles SEPARES (committee, ExecutiveGrants, Teams).
// Aucune logique d'autorisation — affichage uniquement.

import type { GestAdminRole } from '../../hooks/useAdminAudit';
import type { AdminMember, AdminMemberTeam } from '../../hooks/useAdminMembers';
import type { ExecutiveGrantRow } from '../../hooks/useAdminExecutiveGrants';
import { derivePersonRole } from '../org/derivePersonRole';
import { derivePersonQualities } from '../org/derivePersonQualities';
import type { DerivedPersonRole } from '../org/derivePersonRole';

export interface GovernancePersonRow {
  email: string;
  fullName: string | null;
  /** Role unique (precedence) — conserve pour compat tests / tri ; gouvernance UI utilise qualities. */
  derivedRole: GestAdminRole | null;
  /** Qualites cumulees — ecran Acces & gouvernance uniquement (lot C). */
  qualities: DerivedPersonRole[];
  /** null = profil membre absent ou non porte — jamais de defaut optimiste (doctrine lot 2). */
  isEmployed: boolean | null;
  /** Siege CE (EmployeeProfiles.committee = ce) */
  ceSeat: boolean;
  /** Grant CE actif (ExecutiveGrants) */
  ceGrantActive: boolean;
  grantId: string | null;
  grantCreatedAt: string | null;
  grantIsRevoked: boolean;
  committee: 'ce' | 'cg' | null;
  /** Equipes dirigees (vue Directeurs) — read-only derive de Teams.director_email */
  directedTeamNames: string[];
}

export function normGovernanceEmail(email: string): string {
  return email.toLowerCase().trim();
}

export function buildDirectorEmailSet(teams: readonly AdminMemberTeam[]): ReadonlySet<string> {
  return new Set(
    teams.flatMap((t) => (t.directorEmail ? [normGovernanceEmail(t.directorEmail)] : [])),
  );
}

function memberRoleInput(
  member: Pick<AdminMember, 'email' | 'systemCRole'> | undefined,
  directorEmails: ReadonlySet<string>,
): { systemCRole?: AdminMember['systemCRole']; isTeamDirector: boolean } | null {
  if (!member) return null;
  return {
    systemCRole: member.systemCRole,
    isTeamDirector: directorEmails.has(normGovernanceEmail(member.email)),
  };
}

function deriveMemberRole(
  member: Pick<AdminMember, 'email' | 'systemCRole'> | undefined,
  directorEmails: ReadonlySet<string>,
): GestAdminRole | null {
  const input = memberRoleInput(member, directorEmails);
  if (!input) return null;
  return derivePersonRole(input);
}

function deriveMemberQualities(
  member: Pick<AdminMember, 'email' | 'systemCRole'> | undefined,
  directorEmails: ReadonlySet<string>,
): DerivedPersonRole[] {
  const input = memberRoleInput(member, directorEmails);
  if (!input) return [];
  return derivePersonQualities(input);
}

function displayNameSort(a: GovernancePersonRow, b: GovernancePersonRow): number {
  return (a.fullName ?? a.email).localeCompare(b.fullName ?? b.email, 'fr');
}

/** CE : union sieges committee=ce + grants (actifs et historiques). */
export function buildCeGovernanceRows(
  members: readonly AdminMember[],
  grants: readonly ExecutiveGrantRow[],
  directorEmails: ReadonlySet<string>,
): GovernancePersonRow[] {
  const memberByEmail = new Map(members.map((m) => [normGovernanceEmail(m.email), m]));
  const rows = new Map<string, GovernancePersonRow>();

  for (const m of members) {
    if (m.committee !== 'ce') continue;
    const key = normGovernanceEmail(m.email);
    rows.set(key, {
      email: m.email,
      fullName: m.fullName,
      derivedRole: deriveMemberRole(m, directorEmails),
      qualities: deriveMemberQualities(m, directorEmails),
      isEmployed: m.isEmployed,
      ceSeat: true,
      ceGrantActive: false,
      grantId: null,
      grantCreatedAt: null,
      grantIsRevoked: false,
      committee: 'ce',
      directedTeamNames: [],
    });
  }

  for (const g of grants) {
    const key = normGovernanceEmail(g.granteeEmail);
    const member = memberByEmail.get(key);
    const existing = rows.get(key);
    if (existing) {
      if (g.isActive) {
        existing.ceGrantActive = true;
        existing.grantId = g.id;
        existing.grantCreatedAt = g.createdAt;
        existing.grantIsRevoked = false;
      } else if (!existing.grantCreatedAt) {
        existing.grantId = g.id;
        existing.grantCreatedAt = g.createdAt;
        existing.grantIsRevoked = true;
      }
      if (!existing.fullName && member) existing.fullName = member.fullName;
      if (!existing.derivedRole) existing.derivedRole = deriveMemberRole(member, directorEmails);
      if (existing.qualities.length === 0) {
        existing.qualities = deriveMemberQualities(member, directorEmails);
      }
      continue;
    }
    rows.set(key, {
      email: member?.email ?? g.granteeEmail,
      fullName: member?.fullName ?? null,
      derivedRole: deriveMemberRole(member, directorEmails),
      qualities: deriveMemberQualities(member, directorEmails),
      isEmployed: member ? member.isEmployed : null,
      ceSeat: member?.committee === 'ce',
      ceGrantActive: g.isActive,
      grantId: g.id,
      grantCreatedAt: g.createdAt,
      grantIsRevoked: !g.isActive,
      committee: member?.committee === 'ce' || member?.committee === 'cg' ? member.committee : null,
      directedTeamNames: [],
    });
  }

  return [...rows.values()].sort(displayNameSort);
}

/** CG : adhesion committee=cg seulement — aucune ACL CG. */
export function buildCgGovernanceRows(
  members: readonly AdminMember[],
  directorEmails: ReadonlySet<string>,
): GovernancePersonRow[] {
  return members
    .filter((m) => m.committee === 'cg')
    .map((m) => ({
      email: m.email,
      fullName: m.fullName,
      derivedRole: deriveMemberRole(m, directorEmails),
      qualities: deriveMemberQualities(m, directorEmails),
      isEmployed: m.isEmployed,
      ceSeat: false,
      ceGrantActive: false,
      grantId: null,
      grantCreatedAt: null,
      grantIsRevoked: false,
      committee: 'cg' as const,
      directedTeamNames: [],
    }))
    .sort(displayNameSort);
}

/** Directeurs : vue derivee read-only de gest.Teams.director_email. */
export function buildDirectorGovernanceRows(
  teams: readonly AdminMemberTeam[],
  members: readonly AdminMember[],
  directorEmails: ReadonlySet<string>,
): GovernancePersonRow[] {
  const memberByEmail = new Map(members.map((m) => [normGovernanceEmail(m.email), m]));
  const teamNamesByDirector = new Map<string, { email: string; names: string[] }>();

  for (const team of teams) {
    if (!team.directorEmail) continue;
    const key = normGovernanceEmail(team.directorEmail);
    const entry = teamNamesByDirector.get(key) ?? { email: team.directorEmail, names: [] };
    entry.names.push(team.name);
    teamNamesByDirector.set(key, entry);
  }

  return [...teamNamesByDirector.values()]
    .map(({ email, names }) => {
      const member = memberByEmail.get(normGovernanceEmail(email));
      return {
        email: member?.email ?? email,
        fullName: member?.fullName ?? null,
        derivedRole: deriveMemberRole(member, directorEmails),
        qualities: deriveMemberQualities(member, directorEmails),
        isEmployed: member ? member.isEmployed : null,
        ceSeat: member?.committee === 'ce',
        ceGrantActive: false,
        grantId: null,
        grantCreatedAt: null,
        grantIsRevoked: false,
        committee: member?.committee === 'ce' || member?.committee === 'cg' ? member.committee : null,
        directedTeamNames: [...names].sort((a, b) => a.localeCompare(b, 'fr')),
      } satisfies GovernancePersonRow;
    })
    .sort(displayNameSort);
}
